import {
  MFAChannel,
  PreferenceSelectorState,
} from '@eCommerce/frontend-legacy-shared-library';
import {StatusType} from 'models/StatusTypes';
import {Text} from 'native-base';
import {useEffect, useState} from 'react';
import {maskEmail} from 'utils/utils';

import ErrorPanel from 'components/ErrorPanel';
import PanelContainer, {BoldedText} from 'components/PanelContainer';
import StatusDisplay from 'components/StatusDisplay';
import VerificationCodeInput from 'components/VerificationCodeInput';
import {useIsMD} from 'hooks/useIsMD';
import {useIsOpenWebView} from 'hooks/useIsOpenWebView';

export interface CodeVerificationProps {
  mobilePhone: string | undefined;
  email: string | undefined;
  onCodeEnter: (code: string) => void;
  isCodeInvalid: boolean;
  onResendCode?: () => void;
  clearState?: () => void;
  isVip: boolean;
  isError: boolean;
  isLoading: boolean;
  channel: MFAChannel | undefined;
  isSuccess?: boolean; // For step-up services in order to show a success message
  preferenceSelectorState?: PreferenceSelectorState;
  isOverMaxCodeAttempts: boolean;
  isEnrolling?: boolean;
}

const maskPhoneNumber = (phoneNumber: string) => {
  // assuming phone number has 10 digits
  const slicedNumber = phoneNumber.slice(6);
  return `(***) *** ${slicedNumber}`;
};

const CODE_LENGTH = 6;

const CodeVerification: React.FunctionComponent<CodeVerificationProps> = (
  props,
) => {
  const {
    mobilePhone,
    onCodeEnter,
    isCodeInvalid,
    onResendCode,
    isLoading,
    isError,
    clearState,
    channel,
    email,
    isSuccess,
    preferenceSelectorState,
    isVip,
    isEnrolling,
    isOverMaxCodeAttempts: isOverMaxCodeAttempts,
  } = props;
  const isMD = useIsMD();
  const isEmail = channel === MFAChannel.EMAIL;
  const maskedNumber = maskPhoneNumber(mobilePhone || '');
  const maskedEmail = maskEmail(email || '');
  const maskedContact = isEmail ? maskedEmail : maskedNumber;
  const emailVerificationMessage = `Please check your email for the code sent to: `;
  const phoneVerificationMessage = `Please check your phone for the code sent to: `;
  const genericVerificationMessage = `Please enter the code that was sent to your preferred contact method`;
  const bodyTextProps = {color: 'tuna', fontSize: '14px', fontFamily: 'body'};
  const isOpenWebView = useIsOpenWebView();

  const bodyText = (
    <Text {...bodyTextProps}>
      <Text {...bodyTextProps}>
        {!(email || mobilePhone)
          ? genericVerificationMessage
          : isEmail
          ? emailVerificationMessage
          : phoneVerificationMessage}
        {(email || mobilePhone) && (
          <Text {...bodyTextProps} fontWeight="bold">
            {!isMD && '\n'}
            {maskedContact}
          </Text>
        )}
        . Standard message and data rates may apply.
      </Text>
    </Text>
  );

  const [lastCode, setLastCode] = useState<string>('');
  const [retryInProgress, setRetryInProgress] = useState(false);

  const onRetryPress = () => {
    setRetryInProgress(true);

    if (lastCode.length === CODE_LENGTH) {
      onCodeEnter(lastCode);
    } else {
      clearState && clearState();
    }
  };

  const onCodeComplete = (code: string) => {
    setLastCode(code);
    onCodeEnter(code);
  };

  // Reset the state of the retry in progress flag
  useEffect(() => {
    if (isError) {
      setRetryInProgress(false);
    }
  }, [isError]);

  if (isError || retryInProgress) {
    return (
      <ErrorPanel
        onRetryPress={onRetryPress}
        onCancelPress={undefined}
        retryInProgress={retryInProgress}
      />
    );
  }

  const enrollmentDescriptionText =
    'Your enrollment was successful.' +
    (isOpenWebView ? ' You may now close this window.' : '');

  return (
    <PanelContainer
      title="Verification code sent"
      bodyText={bodyText}
      legalCopyText={BoldedText({
        fontSize: '12px',
        text: 'The verification code expires in 60 minutes.',
        boldText: '60',
        lineHeight: '24px',
      })}
    >
      {isSuccess ? ( // Display a success message for step-up services
        <StatusDisplay
          minHeight="248px"
          statusType={StatusType.SUCCESS}
          titleText="SUCCESS"
          upperDescriptionText={
            isEnrolling ? enrollmentDescriptionText : undefined
          }
        />
      ) : (
        <VerificationCodeInput
          onCodeEnter={onCodeComplete}
          isCodeInvalid={isCodeInvalid}
          onResendCode={onResendCode}
          isLoading={isLoading}
          channel={channel}
          isVip={isVip}
          isOverMaxCodeAttempts={isOverMaxCodeAttempts}
        />
      )}
    </PanelContainer>
  );
};

export default CodeVerification;
